import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  Spinner,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { useThemeColors } from '@/hooks/useThemeColors'
import { WaybillForm } from '@/components/forms/WaybillForm'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'

export default function WaybillEditPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const { bookingId, id } = useParams<{ bookingId: string; id: string }>()
  const { showError } = useToastNotification()
  const [waybillId, setWaybillId] = useState<number | null>(null)
  const [bookingData, setBookingData] = useState<any>(null)
  const [isValidating, setIsValidating] = useState(true)
  const isBookingComplete = bookingData?.is_complete === true

  useEffect(() => {
    if (!id) {
      navigate('/bookings', { replace: true })
      return
    }

    const parsedId = Number(id)
    if (Number.isNaN(parsedId)) {
      navigate('/bookings', { replace: true })
      return
    }

    const fetchData = async () => {
      try {
        // Fetch booking data if bookingId is provided
        if (bookingId) {
          const bookingParsedId = Number(bookingId)
          if (!Number.isNaN(bookingParsedId)) {
            const { data: bookingRes } = await apiClient.get(`/bookings/${bookingParsedId}`)
            const booking = bookingRes?.data || bookingRes || {}
            setBookingData(booking)
          }
        }
        setWaybillId(parsedId)
      } catch (error: any) {
        showError(error)
      } finally {
        setIsValidating(false)
      }
    }

    fetchData()
  }, [id, bookingId, navigate, showError])

  const handleSuccess = () => {
    if (bookingId) {
      navigate(`/bookings/waybills/${bookingId}`)
    } else {
      navigate('/bookings')
    }
  }

  const handleCancel = () => {
    if (bookingId) {
      navigate(`/bookings/waybills/${bookingId}`)
    } else {
      navigate('/bookings')
    }
  }


  if (isValidating || waybillId === null) {
    return (
      <AdminLayout>
        <Box p={4}>
          <Card>
            <CardBody>
              <VStack spacing={4} py={8}>
                <Spinner size="xl" color="blue.500" />
                <Text>Loading...</Text>
              </VStack>
            </CardBody>
          </Card>
        </Box>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <Box p={4} h="full" overflow="hidden" display="flex" flexDirection="column">
        <Card h="full" display="flex" flexDirection="column">
          <CardHeader flexShrink={0}>
            <Heading size="md">Edit Waybill</Heading>
            <Text fontSize="sm" color={colors.text} mt={1}>
              Update waybill details
            </Text>
          </CardHeader>
          <CardBody overflowY="auto" flex="1" minH={0}>
            {isBookingComplete ? (
              <VStack spacing={4} align="stretch">
                <Text>
                  This waybill can no longer be edited because the booking is already completed.
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Please return to the waybills list.
                </Text>
              </VStack>
            ) : (
              <WaybillForm
                waybillId={waybillId}
                bookingId={bookingData ? Number(bookingId) : undefined}
                bookingReferenceNumber={bookingData?.reference_number}
                shippingLineId={bookingData?.shipping_line_id}
                shippingLineName={bookingData?.shipping_line?.name}
                cypaFromName={bookingData?.cypa_from?.name}
                cypaToName={bookingData?.cypa_to?.name}
                cypaFromId={bookingData?.cypa_id_from}
                cypaToId={bookingData?.cypa_id_to}
                onSuccess={handleSuccess}
                onCancel={handleCancel}
              />
            )}
          </CardBody>
        </Card>
      </Box>

      <Modal isOpen={isBookingComplete} onClose={handleCancel} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Booking Completed</ModalHeader>
          <ModalBody>
            <Text>This waybill can no longer be edited because the booking is already completed.</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={handleCancel}>
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}

